import { useCallback, useContext, useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useParams } from 'react-router-dom';
import { OrganizationContext } from '../App';
import FormEditor from '../components/Form/Editor/FormEditor';
import PublicSubmissionDetailTabs from '../components/PublicSubmissions/PublicSubmissionDetailTabs';
import ApiErrorMessages from '../components/UI/ApiErrorMessages';
import IconButton from '../components/UI/IconButton';
import Loader from '../components/UI/Loader';
import EditableFormField from '../models/pageModels/EditableFormField';
import FormInstance from '../models/serviceModels/FormInstance';
import ParticipantPublicSubmission from '../models/serviceModels/ParticipantPublicSubmission';
import { PublicSubmissionFollowUpsResponse } from '../models/serviceModels/PublicSubmissionFollowUp';
import { mapFormFieldToEditableFormField } from '../mappers/formFieldMapper';
import {
    getClientPublicSubmissions,
    getForm,
    getFormInstance
} from '../services/formsService';
import { getVisibleSubmissionFormFields } from '../utils/publicSubmissionFields';
import './ParticipantPublicSubmissions.css';

interface ParticipantPublicSubmissionsProps {
    clientId?: string;
    collapsible?: boolean;
}

const ParticipantPublicSubmissions = ({ clientId, collapsible = false }: ParticipantPublicSubmissionsProps) => {
    const { id: routeClientId = '' } = useParams();
    const id = clientId || routeClientId;
    const organization = useContext(OrganizationContext);
    const [submissions, setSubmissions] = useState<ParticipantPublicSubmission[]>([]);
    const [selected, setSelected] = useState<ParticipantPublicSubmission>();
    const [selectedInstance, setSelectedInstance] = useState<FormInstance>();
    const [instanceFields, setInstanceFields] = useState<EditableFormField[]>([]);
    const [loading, setLoading] = useState(true);
    const [loadingDetail, setLoadingDetail] = useState(false);
    const [apiError, setApiError] = useState<any>();
    const [detailApiError, setDetailApiError] = useState<any>();
    const [expanded, setExpanded] = useState(!collapsible);

    const selectSubmission = useCallback((submission: ParticipantPublicSubmission) => {
        setSelected(submission);
        setSelectedInstance(undefined);
        setInstanceFields([]);
        setDetailApiError(undefined);
        setLoadingDetail(true);

        Promise.all([
            getForm(submission.formId, submission.formVersionId),
            getFormInstance(submission.formId, submission.formInstanceId)
        ]).then(([form, instance]) => {
            const visibleFields = getVisibleSubmissionFormFields(form.fields, organization.value);
            setSelectedInstance(instance);
            setInstanceFields(visibleFields.map(field => {
                const instanceField = instance.instanceFields.find(value => value.name === field.name);
                return mapFormFieldToEditableFormField(
                    field,
                    instanceField?.value || '',
                    instanceField?.id || '',
                    instanceField?.files || []
                );
            }));
        }).catch(setDetailApiError).finally(() => setLoadingDetail(false));
    }, [organization.value]);

    useEffect(() => {
        if (!id) return;

        setLoading(true);
        setApiError(undefined);
        setDetailApiError(undefined);
        setSubmissions([]);
        setSelected(undefined);
        setSelectedInstance(undefined);
        setInstanceFields([]);
        setExpanded(!collapsible);
        getClientPublicSubmissions(id).then(result => {
            setSubmissions([...result].sort((left, right) =>
                new Date(right.submissionDate).getTime() - new Date(left.submissionDate).getTime()
            ));
        }).catch(setApiError).finally(() => setLoading(false));
    }, [id, selectSubmission, collapsible]);

    useEffect(() => {
        if (expanded && submissions.length > 0 && !selected) {
            selectSubmission(submissions[0]);
        }
    }, [expanded, submissions, selected, selectSubmission]);

    const handleFollowUpsChanged = (response: PublicSubmissionFollowUpsResponse) => {
        if (!selected) return;

        const openCount = response.followUps.filter(followUp => followUp.status === 'OPEN').length;
        const submittedCount = response.followUps.filter(followUp => followUp.status === 'SUBMITTED').length;
        setSubmissions(current => current.map(submission => submission.id === selected.id ? {
            ...submission,
            followUpStatus: response.followUpStatus,
            followUpCount: response.followUps.length,
            openFollowUpCount: openCount,
            submittedFollowUpCount: submittedCount
        } : submission));
    };

    const submissionContent = <>
        <p>Original public submissions and their follow-up documentation are grouped together below.</p>
        <ApiErrorMessages error={apiError} />
        {loading && <Loader />}
        {!loading && !apiError && submissions.length === 0 && <p>No public submissions are linked to this participant.</p>}
        {!loading && submissions.length > 0 && <div className="participant-public-submissions-layout">
            <div className="participant-public-submission-list" aria-label="Linked public submissions">
                {submissions.map(submission => <button
                        key={submission.id}
                        type="button"
                        className={`participant-public-submission-item${selected?.id === submission.id ? ' active' : ''}`}
                        aria-pressed={selected?.id === submission.id}
                        onClick={() => selectSubmission(submission)}>
                        <span className="participant-public-submission-name text-bold">{submission.formName}</span>
                        <span className="participant-public-submission-metadata">
                            Original submission: {format(new Date(submission.submissionDate), 'MM/dd/yyyy')}
                        </span>
                        <span className="participant-public-submission-metadata">
                            {submission.followUpCount} follow-up {submission.followUpCount === 1 ? 'request' : 'requests'}
                        </span>
                    </button>)}
            </div>
            <section className="participant-public-submission-detail" aria-live="polite">
                <ApiErrorMessages error={detailApiError} />
                {loadingDetail && <Loader />}
                {selected && selectedInstance && !loadingDetail && <>
                    <h3>{selected.formName}</h3>
                    <PublicSubmissionDetailTabs
                        formInstanceId={selected.formInstanceId}
                        formName={selected.formName}
                        instance={selectedInstance}
                        processed={true}
                        readOnly={true}
                        onFollowUpsChanged={handleFollowUpsChanged}
                        originalContent={<FormEditor fields={instanceFields} columnNumber={2} disabled={true} />} />
                </>}
            </section>
        </div>}
    </>;

    if (collapsible) {
        const contentId = 'participant-public-submissions-content';

        return <section className="participant-public-submissions participant-public-submissions-section row padding-2 margin-top-3 border-bottom">
            <div className="column">
                <div className="row align-items-center">
                    <div className="column-auto">
                        <IconButton id="participant-public-submissions"
                            className="small flat"
                            icon={expanded ? <i className="ri-arrow-up-s-line" /> : <i className="ri-arrow-down-s-line" />}
                            ariaLabel={`${expanded ? 'Hide' : 'Show'} Public Submissions (${submissions.length})`}
                            ariaExpanded={expanded}
                            ariaControls={contentId}
                            onClick={() => setExpanded(current => !current)} />
                    </div>
                    <div className="column-auto">
                        <h2>Public Submissions ({submissions.length})</h2>
                    </div>
                    {loading && !expanded && <div className="column-auto">
                        <Loader />
                    </div>}
                </div>
                {expanded && <div id={contentId} className="margin-top-3">
                    {submissionContent}
                </div>}
            </div>
        </section>;
    }

    return <div className="participant-public-submissions">
        <h2>Public Submissions</h2>
        {submissionContent}
    </div>;
};

export default ParticipantPublicSubmissions;
